import { roughlyToPlain } from "./../../../utils/roughly-to-plain";
import {
  INIT_ANSWER_CACHE,
  INIT_VALIDATED_ANSWER_CACHE,
  SET_CURRENT_QUESTION_GROUP,
  SET_QUESTIONNAIRE_CONTENTS,
  SET_QUESTIONNAIRE_QG_IDS_NOT_EMP,
  TOGGLE_CACHE_LOCK,
  TOGGLE_PRE_SUBMIT,
  UPSERT_ANSWER,
  UPSERT_VALIDATED_ANSWER,
} from "./../../../store/mutation.type";
import { TOGGLE_QUESTIONNAIRE_CONTENTS } from "@/store/mutation.type";
import Vue from "vue";
import Component from "vue-class-component";
import flattenTree from "@/utils/flattenTree";
import { styleLog } from "@/utils/style-log";
import { stashAnswer } from "@/api/endpoints/answer/stashAnswer";
import { getStashedAnswer } from "@/api/endpoints/answer/getStashedAnswer";
import { submitAnswer } from "@/api/endpoints/answer/submitAnswer";
import { Mixins } from "vue-property-decorator";
import SampleMixin from "../mixins/sample.mixin";
import LocalForageManager from "@/libs/LocalForageManager";

@Component
export default class QuestionnaireMixin extends Mixins(SampleMixin) {
  /**
   * 是否显示目录
   */
  get showQuestionnaireContents(): boolean {
    return this.$store.state.questionnaire.showQuestionnaireContents;
  }
  set showQuestionnaireContents(show: boolean) {
    this.$store.commit(TOGGLE_QUESTIONNAIRE_CONTENTS, show);
  }

  /**
   * 树结构的目录
   */
  get contents() {
    return this.$store.state.questionnaire.contents;
  }
  set contents(contents: any) {
    this.$store.commit(SET_QUESTIONNAIRE_CONTENTS, contents);
  }
  get flattenContents() {
    return flattenTree(this.contents, "children");
  }

  /**
   * 非空的问题组id集合
   */
  get questionGroupNotEmpty() {
    return this.$store.state.questionnaire.questionGroupNotEmpty;
  }
  set questionGroupNotEmpty(ids) {
    this.$store.commit(SET_QUESTIONNAIRE_QG_IDS_NOT_EMP, ids);
  }

  /**
   * 当前激活的问题组
   */
  get currentQuestionGroup() {
    return this.$store.state.questionnaire.currentQuestionGroup;
  }
  set currentQuestionGroup(id: string) {
    this.$store.commit(SET_CURRENT_QUESTION_GROUP, id);
  }

  get currentQuestionGroupPath(): string[] {
    const path = [];
    const { currentQuestionGroup } = this;
    let codePointer = currentQuestionGroup;
    let nodePointer = this.flattenContents.find(
      (c: any) => c.id === codePointer
    );
    while (nodePointer) {
      path.push(nodePointer.name);
      codePointer = nodePointer.parent;
      nodePointer = this.flattenContents.find((c: any) => c.id === codePointer);
    }
    return path.reverse();
  }

  /**
   * 计算下一个问题组的id
   */
  get nextQuestionGroup(): string | null {
    const { flattenContents, currentQuestionGroup } = this;
    const current = flattenContents.find(
      (n: any) => n.id === currentQuestionGroup
    );
    const c_order = (current || {}).order || -1;
    let next = null;
    flattenContents.sort((n1: any, n2: any) => n1.order - n2.order);
    for (let i = 0; i < flattenContents.length; i++) {
      const node = flattenContents[i];
      if (
        node.order < c_order ||
        node.empty ||
        (current && current.id === node.id)
      )
        continue;
      next = node.id;
      // styleLog.normalPrefix(node.name, "Next Question Group");
      break;
    }
    return next;
  }

  /**
   * 下一个问题组的对象
   */
  get nextQuestionGroupObj() {
    return this.flattenContents.find(
      (c: any) => c.id === this.nextQuestionGroup
    );
  }

  /**
   * 计算上一个问题组的id
   */
  get prevQuestionGroup(): string | null {
    const { flattenContents, currentQuestionGroup } = this;
    const current = flattenContents.find(
      (n: any) => n.id === currentQuestionGroup
    );
    const c_order = (current || {}).order || Infinity;
    let prev = null;
    flattenContents.sort((n1: any, n2: any) => n2.order - n1.order);
    for (let i = 0; i < flattenContents.length; i++) {
      const node = flattenContents[i];
      if (
        node.order > c_order ||
        node.empty ||
        (current && current.id === node.id)
      )
        continue;
      prev = node.id;
      // styleLog.normalPrefix(node.name, "Prev Question Group");
      break;
    }
    return prev;
  }
  /**
   * 上一问题组的对象
   */
  get prevQuestionGroupObj() {
    return this.flattenContents.find(
      (c: any) => c.id === this.prevQuestionGroup
    );
  }

  /**
   * 答案的缓存
   */
  get answerCache() {
    return this.$store.state.questionnaire.answerCache;
  }
  set answerCache(payload: {
    key: string;
    value: { code: string; value: any } | null;
  }) {
    this.$store.commit(UPSERT_ANSWER, payload);
  }

  /**
   * 已验证的问题的缓存
   */
  get validatedAnswerCache() {
    return this.$store.state.questionnaire.validatedAnswerCache;
  }
  set validatedAnswerCache(payload: { questionGroupId: string; answer: any }) {
    this.$store.commit(UPSERT_VALIDATED_ANSWER, payload);
  }

  get lockCache() {
    return this.$store.state.questionnaire.lockCache;
  }
  set lockCache(lock: boolean) {
    this.$store.commit(TOGGLE_CACHE_LOCK, lock);
  }

  /**
   * 是否显示提交确认框
   */
  get showPreSubmit(): boolean {
    return this.$store.state.questionnaire.showPreSubmit;
  }
  set showPreSubmit(show: boolean) {
    this.$store.commit(TOGGLE_PRE_SUBMIT, show);
  }

  /**
   * 返回上一个问题组
   */
  redirectPrevQuestionGroup() {
    if (this.prevQuestionGroup)
      this.currentQuestionGroup = this.prevQuestionGroup;
  }
  /**
   * 跳转下一问题组
   */
  redirectNextQuestionGroup() {
    if (this.nextQuestionGroup)
      this.currentQuestionGroup = this.nextQuestionGroup;
  }

  /**
   * 提交暂存问卷的答案
   */
  async submitStashedQuestionnaire(): Promise<boolean> {
    try {
      const answer = roughlyToPlain(this.validatedAnswerCache);
      answer["__version__"] = new Date().getTime();
      const nativeAnswerCacheManger = new LocalForageManager("answer_cache");
      nativeAnswerCacheManger.set(this.currentSampleId, answer);
      const response = await stashAnswer(this.currentSampleId, answer, true);

      if (!response || response.status) throw Error(response.detail);
      return true;
    } catch (err) {
      console.log(err);
      styleLog.dangerPrefix(err.toString());
      return false;
    }
  }

  /**
   * 同步线上的问卷答案
   */
  async pullQuestionnaireAnswerCacheOnline() {
    try {
      const answerOnline = await getStashedAnswer(this.currentSampleId);
      if (!answerOnline || answerOnline.status) {
        this.$store.commit(INIT_VALIDATED_ANSWER_CACHE, {});
        this.$store.commit(INIT_ANSWER_CACHE, {});
        throw Error(answerOnline.detail);
      }
      const nativeAnswerCacheManger = new LocalForageManager("answer_cache");
      const answerOffline = await nativeAnswerCacheManger.get(
        this.currentSampleId
      );

      const same =
        JSON.stringify(answerOnline) === JSON.stringify(answerOffline);
      if (!same) {
        styleLog.warnPrefix(`本地缓存与在线缓存冲突`, "Conflict");
        console.log({
          local: answerOffline?.__version__,
          online: answerOnline?.__version__,
        });
      }

      const answer = same
        ? answerOnline
        : answerOffline?.__version__ > answerOnline?.__version__
        ? answerOffline
        : answerOnline;

      this.$store.commit(INIT_VALIDATED_ANSWER_CACHE, answer);
      this.$store.commit(
        INIT_ANSWER_CACHE,
        Object.keys(answer).reduce(
          (dict: Record<string, any>, groupId: string) => {
            return {
              ...dict,
              ...answer[groupId],
            };
          },
          {}
        )
      );
    } catch (err) {
      styleLog.dangerPrefix(err.toString());
      this.$store.commit(INIT_ANSWER_CACHE, {});
    }
  }

  /**
   * 提交问卷
   */
  async submitQuestionnaire(): Promise<boolean> {
    try {
      const response = await submitAnswer(
        this.currentSampleId,
        this.validatedAnswerCache
      );
      if (!response || response.status) throw Error(response.detail);
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  }
}
